import { useQuery } from '@tanstack/react-query';
import { Card } from '../../components/ui/Card.js';
import { type LngLat, type RoutePoint, ZoneRouteMap } from '../../components/ui/ZoneRouteMap.js';
import { api } from '../../services/api.js';
import { mytClock, mytDayLabel } from '../../utils/myt.js';

/**
 * 416 (#1431, mock-up screens 4 and 8): the rental's geo-fence figures (`zone_mileage` on `GET /bookings/:id`) and,
 * below them, its route (`GET /bookings/:id/route`, staff only) over the zone, for settling a disputed charge while
 * the positions are kept. The charge itself comes with the mileage packages (#1464).
 */
export interface ZoneMileage {
  status: 'measuring' | 'measured' | 'not_measured';
  reason?: 'no_zone' | 'no_cartrack';
  inside: boolean | null;
  km_outside: number;
  gap_minutes: number;
  suspect_jumps: number;
  last_checked_at: string | null;
  ended_at?: string;
}
interface RouteResponse {
  polygon: LngLat[] | null;
  points: RoutePoint[];
  kept_until: string | null;
}

const REASON: Record<NonNullable<ZoneMileage['reason']>, string> = {
  no_zone: 'Not measured: this pickup location has no zone.',
  no_cartrack: 'Not measured: this car is not on Cartrack.',
};
const when = (iso: string) => `${mytDayLabel(iso)}, ${mytClock(iso)}`;

export function ZoneDistanceCard({ bookingId, zone }: { bookingId: string; zone: ZoneMileage }) {
  const measured = zone.status !== 'not_measured';
  const route = useQuery({
    queryKey: ['booking', bookingId, 'route'],
    queryFn: () => api.get<RouteResponse>(`/bookings/${bookingId}/route`),
    enabled: measured,
  });
  const status =
    zone.status === 'measured'
      ? { word: 'Rental ended', tone: 'bg-surface-feature text-steel' }
      : zone.inside === false
        ? { word: 'Outside', tone: 'bg-semantic-warning-bg text-semantic-warning-text' }
        : zone.inside === true
          ? { word: 'Inside', tone: 'bg-semantic-success-bg text-semantic-success-text' }
          : { word: 'Unknown', tone: 'bg-surface-feature text-steel' };

  return (
    <Card data-testid="zone-distance-card" data-jump="Zone">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-steel">
        Zone &amp; distance
      </h2>
      {!measured ? (
        <p data-testid="zone-not-measured" className="text-sm text-steel">
          {REASON[zone.reason ?? 'no_zone']}
        </p>
      ) : (
        <>
          <dl className="grid grid-cols-[150px_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-steel">Zone</dt>
            <dd>
              <span
                data-testid="zone-status"
                className={`rounded-full px-2 py-0.5 text-xs font-semibold ${status.tone}`}
              >
                {status.word}
              </span>
            </dd>
            <dt className="text-steel">
              {zone.status === 'measured' ? 'Km outside' : 'Km outside so far'}
            </dt>
            <dd data-testid="zone-km" className="font-semibold">
              {zone.km_outside.toFixed(1)} km
            </dd>
            <dt className="text-steel">Last checked</dt>
            <dd>{zone.last_checked_at ? when(zone.last_checked_at) : 'Not yet'}</dd>
            <dt className="text-steel">No new position</dt>
            <dd data-testid="zone-gaps">{zone.gap_minutes} min</dd>
            <dt className="text-steel">Suspect jumps</dt>
            <dd data-testid="zone-jumps">{zone.suspect_jumps}</dd>
          </dl>
          <div className="mt-4">
            {route.data && route.data.points.length > 0 ? (
              <>
                <ZoneRouteMap polygon={route.data.polygon} points={route.data.points} />
                <p data-testid="zone-route-note" className="mt-2 text-xs text-muted">
                  Blue inside, orange outside · one point a minute
                  {route.data.kept_until ? ` · kept until ${when(route.data.kept_until)}` : ''}
                </p>
              </>
            ) : route.data ? (
              <p data-testid="zone-route-note" className="text-xs text-muted">
                {zone.status === 'measured' &&
                route.data.kept_until &&
                Date.parse(route.data.kept_until) < Date.now()
                  ? 'The route is no longer kept (30 days after the rental).'
                  : 'No route recorded yet.'}
              </p>
            ) : null}
          </div>
          <p className="mt-3 text-xs text-muted">
            The charge for the km outside is proposed with the mileage packages and confirmed by
            staff.
          </p>
        </>
      )}
    </Card>
  );
}
